/**
 * Calendar, search, quality, and dashboard handlers from App.js (plan 2.9).
 */
import { useCallback, useEffect } from 'react';
import L from 'leaflet';
import moment from 'moment';
import {
  applyNavigationPreset,
  detectActiveNavigationPreset,
  applyFilterChange,
  FILTER_CHANGE_KINDS,
} from '../features/registry';
import { normalizeCoords, escapeHtml } from '../MapRuntime';
import {
  normalizeCalendarView,
  getCalendarDateRange,
  isRouteEventType,
  formatSearchDate,
  expandPeriodToIncludeDate,
} from '../appHelpers';
import { apiUrl, authFetch, authHeaders } from '../config/apiClient';
import { openSuiteDeepLink } from '../features/craftiaSuiteEvents';
import { t } from '../i18n';

export function useAppEventHandlers({
  currentFilters,
  setCurrentFilters,
  setCalendarDate,
  setCalendarView,
  calendarView,
  calendarDate,
  setPendingQualityIssue,
  pendingQualityIssue,
  setHiddenQualityIssueIds,
  showToast,
  fetchDataFromBackend,
  handleRouteSelect,
  mapRef,
  setSelectedConcert,
  setSelectedDiaryEntry,
  setSelectedEntertainment,
  setSelectedMusicDay,
  setIsGithubOpen,
  setIsAnniversaryOpen,
  selectCalendarDate,
  setGlobalSearchTerm,
  setIsGlobalSearchOpen,
  setGlobalSearchActiveIndex,
  isGlobalSearchOpen,
  deferredGlobalSearchTerm,
  globalSearchItems,
  searchPeriodSnapshot,
  setSearchPeriodSnapshot,
  onGoToImport,
}) {
  const openGlobalSearch = useCallback(() => {
    setGlobalSearchActiveIndex(0);
    setIsGlobalSearchOpen(true);
  }, []);

  const closeGlobalSearch = useCallback(() => {
    setIsGlobalSearchOpen(false);
  }, []);

  // --- Event Handlers ---
  const handleFilterChange = useCallback((newFilters) => {
    setCurrentFilters(newFilters);
    if (newFilters.startDate && newFilters.startDate !== currentFilters.startDate) {
      setCalendarDate(moment(newFilters.startDate).toDate());
    }
  }, [currentFilters.startDate]);

  const handleNavigationPresetSelect = useCallback((preset) => {
    const result = applyNavigationPreset(
      preset,
      currentFilters,
      detectActiveNavigationPreset(currentFilters.selectedTypes)
    );
    setCurrentFilters(result.filters);
  }, [currentFilters]);

  const handlePresetWidenPeriod = useCallback(() => {
    setCurrentFilters(prev => ({
      ...prev,
      startDate: '2010-01-01',
      endDate: moment().format('YYYY-MM-DD'),
    }));
  }, []);

  const handleToggleHiddenQualityIssue = useCallback((issueId) => {
    setHiddenQualityIssueIds(current => (
      current.includes(issueId)
        ? current.filter(currentId => currentId !== issueId)
        : [...current, issueId]
    ));
  }, []);

  const handleApplyQualityCorrection = useCallback(async (issue) => {
    if (!issue?.canFix) return;
    setPendingQualityIssue(issue);
  }, []);

  const confirmQualityCorrection = useCallback(async () => {
    const issue = pendingQualityIssue;
    setPendingQualityIssue(null);
    if (!issue?.canFix) return;

    try {
      const response = await authFetch(apiUrl('/api/data-quality/corrections'), {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          issueId: issue.id,
          action: 'delete_route_group',
          target: issue.target,
        }),
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.error || t('web.quality.applyFail'));
      }

      setHiddenQualityIssueIds(current => current.filter(currentId => currentId !== issue.id));
      showToast(t('web.quality.applyOk'));
      await fetchDataFromBackend(currentFilters);
    } catch (err) {
      showToast(`❌ ${err.message || t('web.quality.applyFail')}`);
    }
  }, [pendingQualityIssue, currentFilters, fetchDataFromBackend, showToast]);

  const handleCalendarNavigate = useCallback((date, view) => {
    const nextView = normalizeCalendarView(view || calendarView);
    setCalendarDate(date);
    setCalendarView(nextView);
    setCurrentFilters(prev => ({
      ...prev,
      ...getCalendarDateRange(date, nextView),
    }));
  }, [calendarView]);

  const handleCalendarView = useCallback((view) => {
    const nextView = normalizeCalendarView(view);
    setCalendarView(nextView);
    setCurrentFilters(prev => ({
      ...prev,
      ...getCalendarDateRange(calendarDate, nextView),
    }));
  }, [calendarDate]);

  const handleCalendarDaySelect = useCallback((date) => {
    if (!date) return;
    // Só seleciona o dia (mostra a lista "Eventos do dia"); o mapa só
    // reflete esse dia se o usuário clicar em "Mostrar só este dia".
    setCalendarDate(moment(date).toDate());
  }, []);

  const handleShowOnlyThisDay = useCallback(() => {
    const day = moment(calendarDate).format('YYYY-MM-DD');
    setCurrentFilters(prev => ({ ...prev, startDate: day, endDate: day }));
  }, [calendarDate]);

  const handleCalendarSlotSelect = useCallback((slotInfo) => {
    if (!slotInfo?.start) return;
    handleCalendarDaySelect(slotInfo.start);
  }, [handleCalendarDaySelect]);

  const handleTimelineHighlightSelect = useCallback((date) => {
    if (!date) return;
    setCalendarDate(moment(date).toDate());
    setCalendarView('month');
  }, []);

  const handleCalendarSelectEvent = useCallback(async (event) => {
    if (event?.resource?.type?.startsWith?.('suite:')) {
      openSuiteDeepLink(event);
      return;
    }
    const { type, data, routeId } = event.resource;
    if (mapRef.current) {
        if ((type === 'checkin') && data.lat && data.lon) {
            mapRef.current.flyTo(normalizeCoords([data.lat, data.lon]), 15);
        } else if (type === 'concerts') {
            setSelectedConcert(data);
            if (data.latitude && data.longitude) {
                mapRef.current.flyTo(normalizeCoords([data.latitude, data.longitude]), 15);
            }
        } else if (type === 'diary') {
            setSelectedDiaryEntry(data);
        } else if (type === 'movie' || type === 'shows') {
            if (data?._aggregateDay && data.date) {
              const itemType = type === 'movie' ? 'movie' : 'episode';
              try {
                const params = new URLSearchParams({
                  item_type: itemType,
                  date: data.date,
                  offset: '0',
                  limit: '100',
                });
                const resp = await authFetch(apiUrl(`/api/trakt?${params}`), {
                  headers: authHeaders(),
                });
                if (resp.ok) {
                  const body = await resp.json();
                  const items = body.items || [];
                  if (items.length === 1) {
                    setSelectedEntertainment({ type, data: items[0] });
                    return;
                  }
                  if (items.length > 1) {
                    setSelectedEntertainment({
                      type,
                      data: items[0],
                      dayItems: items,
                      date: data.date,
                    });
                    return;
                  }
                }
              } catch (err) {
                console.error('Failed to load trakt day list', err);
              }
            }
            setSelectedEntertainment({ type, data });
        } else if (type === 'spotify' || type === 'apple_music') {
            if (data?._needsFetch && data.date) {
              const service = type === 'spotify' ? 'spotify' : 'apple_music';
              try {
                const params = new URLSearchParams({
                  service,
                  date: data.date,
                  offset: '0',
                  limit: '500',
                });
                const resp = await authFetch(apiUrl(`/api/music?${params}`), {
                  headers: authHeaders(),
                });
                if (resp.ok) {
                  const body = await resp.json();
                  setSelectedMusicDay({
                    type,
                    ...data,
                    plays: body.items || [],
                    count: body.total ?? data.count,
                  });
                  return;
                }
              } catch (err) {
                console.error('Failed to load music day list', err);
              }
            }
            setSelectedMusicDay({ type, ...data });
        } else if (isRouteEventType(type) && routeId) {
            handleRouteSelect(routeId);
        } else if (isRouteEventType(type) && data && data.length > 0) {
            const points = data.map(p => normalizeCoords([p.lat, p.lon]));
            if (points.length > 0) {
                const polylineBounds = L.polyline(points).getBounds();
                if (polylineBounds.isValid()) {
                    mapRef.current.flyToBounds(polylineBounds, {padding: [50,50], maxZoom: 19});
                }
            }
        }
    }
  }, [handleRouteSelect]);

  const handleGlobalSearchSelect = useCallback((searchItem) => {
    if (!searchItem) return;

    if (searchItem.outOfPeriod || searchItem.action?.type === 'jumpToPeriod') {
      const jumpDate = searchItem.date || searchItem.action?.date;
      if (jumpDate) {
        if (!searchPeriodSnapshot) {
          setSearchPeriodSnapshot({
            startDate: currentFilters.startDate,
            endDate: currentFilters.endDate,
          });
        }
        const nextRange = expandPeriodToIncludeDate(
          currentFilters.startDate,
          currentFilters.endDate,
          jumpDate
        );
        setCurrentFilters(prev => applyFilterChange(prev, {
          kind: FILTER_CHANGE_KINDS.SET_DATE_RANGE,
          startDate: nextRange.startDate,
          endDate: nextRange.endDate,
        }));
        selectCalendarDate(jumpDate);
      }
      closeGlobalSearch();
      return;
    }

    closeGlobalSearch();

    const { action, item, date, type } = searchItem;
    if (date) setCalendarDate(moment(date).toDate());

    if (action?.type === 'focusRoute' && action.routeId) {
      handleRouteSelect(action.routeId);
      return;
    }

    const itemLat = Number(item?.lat);
    const itemLon = Number(item?.lon);
    if (action?.type === 'flyToMarker' && Number.isFinite(itemLat) && Number.isFinite(itemLon) && mapRef.current) {
      const coords = normalizeCoords([itemLat, itemLon]);
      mapRef.current.flyTo(coords, 17);
      L.popup({ closeOnClick: true, autoClose: true })
        .setLatLng(coords)
        .setContent(`<b>${escapeHtml(item.venue || item.name || 'Local')}</b>${item.category ? `<br>${escapeHtml(item.category)}` : ''}${date ? `<br>${moment(date).format('DD/MM/YYYY')}` : ''}`)
        .openOn(mapRef.current);
      return;
    }

    if (type === 'concerts') {
      setSelectedConcert(item);
      const concertLat = Number(item?.latitude);
      const concertLon = Number(item?.longitude);
      if (Number.isFinite(concertLat) && Number.isFinite(concertLon) && mapRef.current) {
        mapRef.current.flyTo(normalizeCoords([concertLat, concertLon]), 15);
      }
      return;
    }

    if (type === 'diary') {
      setSelectedDiaryEntry(item);
      return;
    }

    if (type === 'spotify' || type === 'apple_music') {
      const service = type === 'spotify' ? 'Spotify' : 'Apple Music';
      const day = date || formatSearchDate(item?.played_at).isoDate;
      setSelectedMusicDay({
        type,
        date: day,
        service,
        count: 1,
        plays: [item],
      });
      return;
    }

    if (action?.type === 'openGithub') {
      setIsGithubOpen(true);
      return;
    }

    selectCalendarDate(date);
  }, [
    closeGlobalSearch,
    handleRouteSelect,
    selectCalendarDate,
    searchPeriodSnapshot,
    setSearchPeriodSnapshot,
    currentFilters.startDate,
    currentFilters.endDate,
    setCurrentFilters,
    setCalendarDate,
    mapRef,
    setSelectedConcert,
    setSelectedDiaryEntry,
    setSelectedMusicDay,
    setIsGithubOpen,
  ]);

  const handleRestoreSearchPeriod = useCallback(() => {
    if (!searchPeriodSnapshot) return;
    setCurrentFilters(prev => applyFilterChange(prev, {
      kind: FILTER_CHANGE_KINDS.SET_DATE_RANGE,
      startDate: searchPeriodSnapshot.startDate,
      endDate: searchPeriodSnapshot.endDate,
    }));
    setSearchPeriodSnapshot(null);
  }, [searchPeriodSnapshot, setCurrentFilters, setSearchPeriodSnapshot]);

  const handleAnniversarySelect = useCallback((item) => {
    if (!item) return;
    setIsAnniversaryOpen(false);
    selectCalendarDate(item.start);
    handleCalendarSelectEvent({
      title: item.title,
      start: item.start,
      end: item.end,
      allDay: item.allDay,
      resource: item.resource,
    });
  }, [handleCalendarSelectEvent, selectCalendarDate]);

  const handleDashboardCardAction = useCallback((action) => {
    if (!action?.type) return;

    if (action.type === 'focusDate' && action.payload?.date) {
      selectCalendarDate(action.payload.date);
      return;
    }

    if (action.type === 'focusStreakPeriod' && action.payload?.startDate && action.payload?.endDate) {
      setCurrentFilters(prev => ({
        ...prev,
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      }));
      setCalendarDate(moment(action.payload.startDate).toDate());
      return;
    }

    if (action.type === 'applyPeriodRange' && action.payload?.startDate && action.payload?.endDate) {
      setCurrentFilters(prev => ({
        ...prev,
        startDate: action.payload.startDate,
        endDate: action.payload.endDate,
      }));
      setCalendarDate(moment(action.payload.startDate).toDate());
      return;
    }

    if (action.type === 'goToImport') {
      onGoToImport?.();
      return;
    }

    if (action.type === 'searchArtist' && action.payload?.artist) {
      setGlobalSearchTerm(action.payload.artist);
      openGlobalSearch();
      return;
    }

    if (action.type === 'searchShow' && action.payload?.show) {
      setGlobalSearchTerm(action.payload.show);
      openGlobalSearch();
      return;
    }

    if (action.type === 'focusFrequentPlace' && action.payload?.lat != null && action.payload?.lon != null) {
      const lat = Number(action.payload.lat);
      const lon = Number(action.payload.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon) || !mapRef.current) return;

      mapRef.current.flyTo([lat, lon], 16);
      if (action.payload.lastDate) {
        selectCalendarDate(action.payload.lastDate);
      }
      L.popup({ closeOnClick: true, autoClose: true })
        .setLatLng([lat, lon])
        .setContent(
          `<b>${action.payload.nameApprox || 'Lugar frequente'}</b>` +
          `<br>${action.payload.kindLabel || 'Lugar'}` +
          `<br>${action.payload.count || 0} sinais` +
          (action.payload.lastDate ? t('web.lastVisit.popup', { date: action.payload.lastDate }) : '')
        )
        .openOn(mapRef.current);
      return;
    }
  }, [openGlobalSearch, selectCalendarDate, onGoToImport]);

  useEffect(() => {
    setGlobalSearchActiveIndex(0);
  }, [deferredGlobalSearchTerm, globalSearchItems.length]);

  useEffect(() => {
    if (!isGlobalSearchOpen) return undefined;

    const handleShortcut = (event) => {
      if (event.key === 'Escape') {
        closeGlobalSearch();
      }
    };

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [isGlobalSearchOpen, closeGlobalSearch]);

  useEffect(() => {
    const handleShortcut = (event) => {
      const target = event.target;
      const isTyping = target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
      const isCommandK = event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey);
      const isSlash = event.key === '/' && !isTyping;

      if (!isCommandK && !isSlash) return;
      event.preventDefault();
      openGlobalSearch();
    };

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [openGlobalSearch]);

  const eventPropGetter = useCallback((event) => ({
    style: {
      backgroundColor: event.resource?.color || '#3174ad',
      borderRadius: '5px',
      opacity: event.resource?.muted ? 0.25 : 0.9,
      color: 'white',
      border: '1px solid rgba(0,0,0,0.2)',
      display: 'block',
      padding: '2px 5px',
      fontSize: '0.8em'
    },
  }), []);

  // Destaca visualmente o dia selecionado no grid do calendário.
  const dayPropGetter = useCallback((date) => (
    moment(date).isSame(calendarDate, 'day')
      ? { className: 'rbc-day-selected' }
      : {}
  ), [calendarDate]);

  // Resize map handler


  return {
    openGlobalSearch,
    closeGlobalSearch,
    handleFilterChange,
    handleNavigationPresetSelect,
    handlePresetWidenPeriod,
    handleToggleHiddenQualityIssue,
    handleApplyQualityCorrection,
    confirmQualityCorrection,
    handleCalendarNavigate,
    handleCalendarView,
    handleCalendarDaySelect,
    handleShowOnlyThisDay,
    handleCalendarSlotSelect,
    handleTimelineHighlightSelect,
    handleCalendarSelectEvent,
    handleGlobalSearchSelect,
    handleRestoreSearchPeriod,
    handleAnniversarySelect,
    handleDashboardCardAction,
    eventPropGetter,
    dayPropGetter,
  };
}
